import { useSelector } from 'react-redux';
import findResult, { jsonTryParse } from '@/utils/resultsHelper';
import urls, { prefixUrl } from '@/constants/urls';
import { vocabularyCodes } from '../constants';

const findVocabulary = (vocabularies, code) => findResult(vocabularies, 'all', code);

const useVocabularies = (code) => {
  const { vocabularies } = useSelector(({ vocabularies: v }) => v);
  return { vocabularies, vocabulary: findVocabulary(vocabularies, code) };
};

const useVocabularyTools = () => {
  const { role: { permissions: { vocabularies: { update } } } } = useSelector(({ auth }) => auth);
  return { allowUpdate: update };
};

const useTermsLink = () => {
  const { vocabulary } = useVocabularies(vocabularyCodes.T_AND_C_URL);
  return { href: vocabulary ? vocabulary.value : urls.T_AND_C };
};

const useHomeLink = () => {
  const { vocabulary } = useVocabularies(vocabularyCodes.PORTAL_URL);
  return { href: vocabulary ? prefixUrl(vocabulary.value) : '/' };
};

const useHomePanelCustomContent = () => {
  const { vocabulary } = useVocabularies(vocabularyCodes.LANDING_CUSTOM);
  return { content: vocabulary ? vocabulary.value || vocabulary.original : '' };
};

const useLandingPlaceholder = () => {
  const { vocabularies, vocabulary: message } = useVocabularies(vocabularyCodes.LANDING_MESSAGE);
  const { theme } = useSelector(({ themes }) => themes);
  const title = findVocabulary(vocabularies, vocabularyCodes.LANDING_TITLE) || { title: '', value: '' };
  return {
    message: message ? message.value || message.original : '',
    theme,
    title: title.value || title.original,
  };
};

const usePartnerLink = () => {
  const { vocabularies, vocabulary: href } = useVocabularies(vocabularyCodes.PARTNER_URL);
  const title = findVocabulary(vocabularies, vocabularyCodes.PARTNER_URL_TITLE);
  return { href: href ? prefixUrl(href.value) : null, title: title ? title.value : '' };
};

const useHelpLink = () => {
  const { vocabularies, vocabulary: title } = useVocabularies(vocabularyCodes.KB_URL_TITLE);
  const url = findVocabulary(vocabularies, vocabularyCodes.KB_URL);
  return { title: title ? (title.value || title.original) : '', url: url ? (url.value || url.original) : '' };
};

const useExternalLinks = () => {
  const { vocabularies, vocabulary: name } = useVocabularies(vocabularyCodes.EXTERNAL_URLS_NAME);
  const links = findVocabulary(vocabularies, vocabularyCodes.EXTERNAL_URLS);
  return {
    linksName: name ? name.value : '',
    links: links ? jsonTryParse(links.value, []) : [],
  };
};

const useRequestConfirmationMessage = () => {
  const { vocabulary } = useVocabularies(vocabularyCodes.DAR_CUSTOM_MESSAGE);
  return { content: vocabulary ? vocabulary.value || vocabulary.original : '' };
};

const useCookiePrivacyLink = () => {
  const { vocabulary } = useVocabularies(vocabularyCodes.COOKIE_PRIVACY_URL);
  return { url: vocabulary ? prefixUrl(vocabulary.value) : '' };
};

export default {
  useCookiePrivacyLink,
  useExternalLinks,
  useHelpLink,
  useHomeLink,
  useHomePanelCustomContent,
  useLandingPlaceholder,
  usePartnerLink,
  useRequestConfirmationMessage,
  useTermsLink,
  useVocabularyTools,
};
